import { useEffect, useState } from "react";
import { checkOgImage, type OgImageCheck } from "@/utils/seo";

interface SocialSharePreviewProps {
  imageUrl: string;
  title: string;
  description: string;
  /** Absolute URL of the page being previewed (shown as the card domain). */
  url: string;
  /** Optional heading shown above the card. */
  label?: string;
}

const STATUS_STYLES: Record<OgImageCheck["status"], { icon: string; tone: string }> = {
  loading: { icon: "ri-loader-4-line animate-spin", tone: "text-foreground-500" },
  ok: { icon: "ri-checkbox-circle-line", tone: "text-accent-700" },
  warning: { icon: "ri-error-warning-line", tone: "text-secondary-700" },
  error: { icon: "ri-close-circle-line", tone: "text-secondary-700" },
  empty: { icon: "ri-information-line", tone: "text-foreground-500" },
};

/** A Facebook/WhatsApp-style link card plus a dimension check for the image. */
export default function SocialSharePreview({
  imageUrl,
  title,
  description,
  url,
  label = "Social share preview",
}: SocialSharePreviewProps) {
  const [check, setCheck] = useState<OgImageCheck>({
    status: "loading",
    width: 0,
    height: 0,
    message: "Checking the share image…",
  });

  useEffect(() => {
    let active = true;
    setCheck({ status: "loading", width: 0, height: 0, message: "Checking the share image…" });
    checkOgImage(imageUrl).then((result) => {
      if (active) setCheck(result);
    });
    return () => {
      active = false;
    };
  }, [imageUrl]);

  const style = STATUS_STYLES[check.status];
  const domain = (() => {
    try {
      return new URL(url).hostname.replace(/^www\./, "");
    } catch {
      return "mysoreyellowpages.com";
    }
  })();

  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-foreground-500">{label}</p>

      <div className="mt-2 overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="relative aspect-[1200/630] w-full bg-background-100">
          {imageUrl ? (
            <img src={imageUrl} alt="Social share image preview" className="h-full w-full object-cover object-top" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-xs text-foreground-400">
              No share image set
            </div>
          )}
        </div>
        <div className="border-t border-background-200 px-4 py-3">
          <p className="text-[11px] uppercase tracking-wide text-foreground-400">{domain}</p>
          <p className="mt-1 line-clamp-2 text-sm font-semibold text-foreground-900">{title || "Page title"}</p>
          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-foreground-600">
            {description || "Page description"}
          </p>
        </div>
      </div>

      <p className={`mt-2 inline-flex items-start gap-1.5 text-xs font-medium ${style.tone}`}>
        <i className={`${style.icon} mt-0.5`}></i>
        <span>{check.message}</span>
      </p>
    </div>
  );
}